
import { Property, CiencuadrasApiResponseData } from '../types';
import { transformCdnImageUrl } from './cdnImageUtils';
import { normalizePropertyTags } from './propertyTagUtils';

const MASK_PREFIX = 'CCXP-';

/**
 * Codifica el ID del documento en Base64 para ocultar el patrón original.
 * Reemplaza caracteres no amigables para URLs.
 */
export const encodeId = (id: string): string => {
    if (!id) return 'UNK';
    try {
        // Codificamos a Base64 y limpiamos caracteres problemáticos
        return btoa(id)
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, ''); // Eliminamos el padding de '=' para que se vea más limpio
    } catch (e) {
        return id;
    }
};

/**
 * Decodifica la parte enmascarada para recuperar el ID original de Firestore.
 */
export const decodeId = (maskedPart: string): string => {
    try {
        // Restauramos los caracteres originales de Base64
        let base64 = maskedPart
            .replace(/-/g, '+')
            .replace(/_/g, '/');
        
        // Restauramos el padding de '=' necesario para atob
        while (base64.length % 4) {
            base64 += '=';
        }
        
        return atob(base64);
    } catch (e) {
        console.error("Error decodificando código enmascarado:", e);
        return maskedPart; // Si falla, devolvemos el original por si acaso
    }
};

export const generateMaskedCode = (id: string): string => {
    return `${MASK_PREFIX}${encodeId(id)}`;
};

const isYoutubeVideoUrl = (url: string): boolean => {
  if (!url) return false;
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)/;
  return youtubeRegex.test(url);
};

const constructFullImageUrl = (url: string): string => {
    if (!url || typeof url !== 'string') return '';
    let fullUrl = url;
    if (!fullUrl.toLowerCase().startsWith('http')) {
        if (fullUrl.startsWith('//')) {
            fullUrl = 'https:' + fullUrl;
        } else {
            fullUrl = `https://www.ciencuadras.com${fullUrl.startsWith('/') ? fullUrl : `/${fullUrl}`}`;
        }
    }
    return transformCdnImageUrl(fullUrl);
};

/**
 * Limpia y omite información de contacto (correos y teléfonos de vendedores colombianos) 
 * de las descripciones de los inmuebles.
 */
export const cleanPropertyDescription = (desc: string | undefined): string => {
    if (!desc) return "";
    let cleaned = desc;
    
    // 1. Correos estándar (ej. nombre@ejemplo.com)
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g;
    
    // 2. Correo-dominios de inmobiliarias sin el símbolo @ (ej. omar.poloinmobiliariabogota.com) y variantes con punto final
    const directDomainRegex = /\b[A-Za-z0-9._%+-]+(?:\.[A-Za-z0-9._%+-]+)*inmobiliariabogota\.com\.?/gi;
    
    // 3. Celulares colombianos (10 dígitos que inician con 3, admitiendo guiones o espacios intermedios)
    const phoneRegex = /(?:\+?57)?[-\s]?\b3\d{2}[-\s]?\d{3}[-\s]?\d{4}\b/g;

    // Aplicar reemplazos
    cleaned = cleaned.replace(emailRegex, "");
    cleaned = cleaned.replace(directDomainRegex, "");
    cleaned = cleaned.replace(phoneRegex, "");

    // 4. Limpieza de conectores y preposiciones huérfanos que solían anteceder a los datos eliminados
    cleaned = cleaned.replace(/\s+(?:o\s+)?al\s+(?:teléfono|telefono|cel|celular|correo|correo\s+electrónico|email|contacto)\b/gi, " ");
    cleaned = cleaned.replace(/\s+(?:al\s+)?(?:teléfono|telefono|cel|celular|correo|correo\s+electrónico|email|contacto)\b/gi, " ");
    cleaned = cleaned.replace(/\b(?:comunicate|comunícate|contactenos|contáctenos|escribe|escríbenos|llámanos|llamame)\s+a?\s*$/gi, " ");

    // Eliminar espacios adicionales, puntos duplicados o puntuación sobrante
    cleaned = cleaned.replace(/\s+/g, " ");
    cleaned = cleaned.replace(/\s*([.,;])\s*/g, "$1 ").replace(/\.{2,}/g, ".");
    
    return cleaned.trim();
};

// --- Firestore to App Adapter ---
export const transformFirestoreToAppProperty = (docData: any, docId: string): Property => {
    const data = docData || {};
    
    const currentYear = new Date().getFullYear();
    const constructionYear = data.anio_construccion ? Number(data.anio_construccion) : null;
    let antiguedad: number | null = null;
    if (constructionYear) {
        antiguedad = currentYear - constructionYear;
    } else if (data.antiguedad !== undefined && data.antiguedad !== null) {
        antiguedad = Number(data.antiguedad);
    }

    let images: string[] = [];
    let videoUrl: string | null = data.video_youtube_url || data.link_recorrido_virtual || null;

    const rawImgs = Array.isArray(data.imagenes) && data.imagenes.length > 0 ? data.imagenes : (Array.isArray(data.images) ? data.images : []);
    if (rawImgs.length > 0) {
        rawImgs.forEach((url: any) => {
            const finalUrl = typeof url === 'string' ? url : (url.url || '');
            const fullUrl = constructFullImageUrl(finalUrl);
            if (fullUrl) {
                if (isYoutubeVideoUrl(fullUrl)) {
                    if (!videoUrl) videoUrl = fullUrl;
                } else {
                    images.push(fullUrl);
                }
            }
        });
    }

    let status = data.status || 'Activo';
    if (data.activo === 1) status = 'Inactivo'; 
    
    let areaConstruida = "";
    if (data.area_construida !== undefined && data.area_construida !== null) {
        areaConstruida = String(data.area_construida);
    } else if (data.area_construida_original) {
        areaConstruida = String(data.area_construida_original);
    } else if (data.area) {
        areaConstruida = String(data.area);
    }

    let areaPrivadaStr = data.area_privada ? String(data.area_privada) : "";

    const isAreaConstruidaEmpty = !areaConstruida || areaConstruida.replace(/m²|\s/g, '').trim() === "" || areaConstruida.replace(/m²|\s/g, '').trim() === "0" || areaConstruida.replace(/m²|\s/g, '').trim() === "0.0";
    const isAreaPrivadaEmpty = !areaPrivadaStr || areaPrivadaStr.replace(/m²|\s/g, '').trim() === "" || areaPrivadaStr.replace(/m²|\s/g, '').trim() === "0" || areaPrivadaStr.replace(/m²|\s/g, '').trim() === "0.0";

    if (isAreaConstruidaEmpty && !isAreaPrivadaEmpty) {
        areaConstruida = areaPrivadaStr;
    } else if (isAreaPrivadaEmpty && !isAreaConstruidaEmpty) {
        areaPrivadaStr = areaConstruida;
    }

    if (areaConstruida && !areaConstruida.toLowerCase().includes('m')) {
        areaConstruida += ' m²';
    }
    if (areaPrivadaStr && !areaPrivadaStr.toLowerCase().includes('m')) {
        areaPrivadaStr += ' m²';
    }

    const barrio = data.nombre_barrio || data.barrio || data.neighborhoodName || null;
    const localidad = data.localidad || data.localidad_integrador || data.nombre_localidad || data.locality || null;
    const tipo = data.tipo_inmueble || data.tipoinmueble || data.propertyType || "SIN TIPO DE INMUEBLE";
    const originalCode = data.codigo != null ? String(data.codigo) : "SIN ID";

    let numericArea = data.area_construida !== undefined && data.area_construida !== null ? Number(data.area_construida) : (data.area ? Number(data.area) : 0);
    if ((!numericArea || numericArea === 0) && data.area_privada) {
        const cleanPriv = String(data.area_privada).replace(/m²|\s/g, '');
        numericArea = Number(cleanPriv) || 0;
    }

    const rawDesc = data.descripcion || "";
    const cleanDesc = cleanPropertyDescription(rawDesc);

    return {
        id: docId,
        codigo: originalCode,
        codigo_enmascarado: generateMaskedCode(docId),
        ciudad: data.ciudad || "SIN CIUDAD",
        tipoinmueble: tipo,
        direccion: data.direccion || "SIN DIRECCIÓN",
        descripcion: cleanDesc,
        precio_venta: Number(data.precio_venta) || 0,
        valor_administracion: Number(data.valor_administracion) || 0,
        num_habitaciones: Number(data.num_habitaciones) || 0,
        num_banos: Number(data.num_banos) || 0,
        area_construida_original: areaConstruida,
        area_construida: numericArea,
        area_privada: areaPrivadaStr || null,
        status: status,
        // TRANSFORMACIÓN: Cambiamos "-" por "_" para que coincida con las opciones del dropdown del UI
        owner: (data.owner || data.duenio || "").replace(/-/g, '_'),
        latitude: data.latitude ? Number(data.latitude) : (data.latitud ? Number(data.latitud) : null),
        longitude: data.longitude ? Number(data.longitude) : (data.longitud ? Number(data.longitud) : null),
        localidad: localidad,
        neighborhoodName: barrio,
        estrato: Number(data.estrato) || 0,
        images: images,
        technicalSheetUrl: data.technicalSheetUrl || "#",
        photosZipUrl: data.photosZipUrl || "#",
        brochureUrl: data.brochureUrl || "#",
        constructionYear: constructionYear,
        antiguedad: antiguedad,
        fechaAgregadoNuevo: data.fecha_agregado_nuevo || data.fechaAgregadoNuevo || data.fecha_creacion || data.createdAt || null,
        fecha_agregado_nuevo: data.fecha_agregado_nuevo || data.fechaAgregadoNuevo || null,
        fecha_creacion: data.fecha_creacion || data.createdAt || null,
        num_parqueaderos: Number(data.num_parqueaderos) || 0,
        video_youtube_url: videoUrl,
        vigilanceType: data.tipo_vigilancia || data.vigilanceType || data.vigilance || null,
        ascensor: (() => {
            if (data.ascensor === true) return 1;
            if (data.ascensor === false) return 0;
            if (data.ascensor !== undefined && data.ascensor !== null && data.ascensor !== '') {
                const parsed = Number(data.ascensor);
                return !isNaN(parsed) ? parsed : null;
            }
            if (data.num_ascensores !== undefined && data.num_ascensores !== null && data.num_ascensores !== '') {
                const parsed = Number(data.num_ascensores);
                return !isNaN(parsed) ? parsed : null;
            }
            return null;
        })(),
        num_ascensores: (() => {
            if (data.ascensor !== undefined && data.ascensor !== null && data.ascensor !== '') {
                const parsed = Number(data.ascensor);
                return !isNaN(parsed) ? parsed : null;
            }
            if (data.num_ascensores !== undefined && data.num_ascensores !== null && data.num_ascensores !== '') {
                const parsed = Number(data.num_ascensores);
                return !isNaN(parsed) ? parsed : null;
            }
            return null;
        })(),
        
        // --- MAPEO DE CONTACTO CORREGIDO (Lectura DB -> App) ---
        nombre_cliente: data.nombre_contacto || data.contacto_nombre || data.nombre_cliente || null,
        telefono_cliente: data.celular_contacto || data.telefono_contacto || data.contacto_telefono || data.telefono_cliente || null,
        correo_1: data.correo_contacto || data.correo_1 || null,
        
        es_visible_broker: data.es_visible_broker === true,
        es_preventa: data.es_preventa === true, // CAMBIO: Mapeo explícito de es_preventa
        num_balcones: Number(data.num_balcones) || 0,
        codigo_brp: data.codigo_brp_o_inmobiliaria != null ? String(data.codigo_brp_o_inmobiliaria) : (data.codigo_brp != null ? String(data.codigo_brp) : null),
        commissionRate: data.commissionRate || 0,
        estrategiaComercial: data.estrategiaComercial || null,
        features: data.features || [],
        tag: normalizePropertyTags(data.tag, data.tags || data.etiquetas),
        tags: normalizePropertyTags(data.tag, data.tags || data.etiquetas),
        codigo_actual: data.codigo_actual != null ? String(data.codigo_actual) : null,
        numero_matricula: data.numero_matricula || null,
        numero_predial: data.numero_predial || null,
        piso: data.piso || null,
        propertyCondition: data.propertyCondition || data.estado || null,
        
        // --- COMISION OVERRIDES ---
        comision_override_broker: data.comision_override_broker ?? data.brokerCommissionRate ?? null,
        comision_override_premium: data.comision_override_premium ?? data.premiumCommissionRate ?? null,
        comision_override_interno: data.comision_override_interno ?? data.commissionInterno ?? null,
        tiene_oferta: data.tiene_oferta === true,
        valor_oferta: data.valor_oferta ?? null,
        tabla_comisiones: Array.isArray(data.tabla_comisiones) ? data.tabla_comisiones : (Array.isArray(data.commissionTiers) ? data.commissionTiers : null),

        // Campos de encargado (se mantienen igual)
        nombre_encargado: data.nombre_encargado || null,
        telefono_encargado: data.telefono_encargado || null,
        disponibilidad_visitas: data.disponibilidad_visitas || null,
        observaciones_internas: data.observaciones_internas || null,
    };
};

export const mapApiDataToProperty = (apiData: CiencuadrasApiResponseData, existingProperty?: Property): Partial<Property> => {
    const rawApiDesc = apiData.descripcion || existingProperty?.descripcion || "";
    const cleanApiDesc = cleanPropertyDescription(rawApiDesc);

    const mapped: Partial<Property> = {
        codigo: apiData.codigo || existingProperty?.codigo,
        ciudad: apiData.ciudad || existingProperty?.ciudad,
        tipoinmueble: apiData.tipoinmueble || existingProperty?.tipoinmueble,
        direccion: apiData.direccion || existingProperty?.direccion,
        descripcion: cleanApiDesc,
        precio_venta: apiData.precio_venta ?? existingProperty?.precio_venta,
        valor_administracion: apiData.valor_administracion ?? existingProperty?.valor_administracion,
        num_habitaciones: apiData.num_habitaciones ?? existingProperty?.num_habitaciones,
        num_banos: apiData.num_banos ?? existingProperty?.num_banos,
        area_construida_original: apiData.area_construida_original ? String(apiData.area_construida_original) + (String(apiData.area_construida_original).includes('m') ? '' : ' m²') : existingProperty?.area_construida_original,
        status: existingProperty?.status || 'Activo',
        owner: apiData.nombre_usuario || existingProperty?.owner || 'API Ciencuadras (Simulado)',
    };

    const images: string[] = [];
    let videoUrl: string | null = apiData.video_youtube_url || existingProperty?.video_youtube_url || null;

    if (apiData.imagenes && apiData.imagenes.length > 0) {
        apiData.imagenes.forEach(img => {
            if (img && img.url) {
                let sanitizedUrl = img.url;
                if (sanitizedUrl.endsWith(':1')) sanitizedUrl = sanitizedUrl.slice(0, -2);
                const fullUrl = constructFullImageUrl(sanitizedUrl);
                if (isYoutubeVideoUrl(fullUrl) && !videoUrl) videoUrl = fullUrl;
                else if (!isYoutubeVideoUrl(fullUrl)) images.push(fullUrl);
            }
        });
    }
    
    mapped.images = images.length > 0 ? images : (existingProperty?.images?.filter(img => !isYoutubeVideoUrl(img)) || []);
    mapped.video_youtube_url = videoUrl;
    if (apiData.estrato) mapped.estrato = parseInt(apiData.estrato, 10);
    if (apiData.num_parqueaderos !== undefined) mapped.num_parqueaderos = apiData.num_parqueaderos;
    mapped.neighborhoodName = apiData.barrio_integrador || apiData.barrio || apiData.barrio_usuario || existingProperty?.neighborhoodName;
    mapped.localidad = apiData.localidad_integrador || apiData.localidad || existingProperty?.localidad;
    if(apiData.latitud) mapped.latitude = apiData.latitud;
    if(apiData.longitud) mapped.longitude = apiData.longitud;
    
    const normalizedApiTags = normalizePropertyTags((apiData as any).tag, (apiData as any).tags || existingProperty?.tag || existingProperty?.tags);
    if (normalizedApiTags.length > 0) {
        mapped.tag = normalizedApiTags;
        mapped.tags = normalizedApiTags;
    }
    
    return mapped;
};

/**
 * Transforma un objeto Property (o actualización parcial) a la estructura exacta de documento en Firestore (coleccion_inmuebles),
 * garantizando persistencia bidireccional consistente tanto en campos canónicos de base de datos como en espejos de compatibilidad.
 */
export const transformAppPropertyToFirestore = (propertyData: Partial<Property> | Record<string, any>): Record<string, any> => {
    const raw: any = propertyData || {};
    const doc: Record<string, any> = {};

    // 1. Identificadores y Códigos
    if (raw.codigo !== undefined) doc.codigo = String(raw.codigo || '').trim();
    const brp = raw.codigo_brp ?? raw.codigo_brp_o_inmobiliaria;
    if (brp !== undefined) {
        const val = brp !== null && brp !== '' ? String(brp).trim() : null;
        doc.codigo_brp_o_inmobiliaria = val;
        doc.codigo_brp = val;
    }
    if (raw.codigo_actual !== undefined) doc.codigo_actual = raw.codigo_actual;

    // 2. Clasificación y Estado
    const tipo = raw.tipoinmueble ?? raw.tipo_inmueble;
    if (tipo !== undefined) {
        doc.tipo_inmueble = tipo;
        doc.tipoinmueble = tipo;
    }
    if (raw.status !== undefined) doc.status = raw.status;

    // 3. Ubicación
    if (raw.ciudad !== undefined) doc.ciudad = raw.ciudad;
    if (raw.localidad !== undefined) {
        doc.localidad = raw.localidad;
        doc.localidad_integrador = raw.localidad;
    }
    const barrio = raw.neighborhoodName ?? raw.nombre_barrio ?? raw.barrio;
    if (barrio !== undefined) {
        doc.nombre_barrio = barrio;
        doc.barrio = barrio;
        doc.neighborhoodName = barrio;
    }
    if (raw.direccion !== undefined) doc.direccion = raw.direccion;
    if (raw.latitude !== undefined) {
        doc.latitude = raw.latitude;
        doc.latitud = raw.latitude;
    }
    if (raw.longitude !== undefined) {
        doc.longitude = raw.longitude;
        doc.longitud = raw.longitude;
    }

    // 4. Propietario / Dueño
    const ownerVal = raw.owner ?? raw.duenio;
    if (ownerVal !== undefined) {
        doc.owner = ownerVal;
        doc.duenio = ownerVal;
    }

    // 5. Precios & Finanzas
    if (raw.precio_venta !== undefined) doc.precio_venta = Number(raw.precio_venta) || 0;
    if (raw.precio_original !== undefined) doc.precio_original = Number(raw.precio_original) || 0;
    if (raw.valor_administracion !== undefined) doc.valor_administracion = Number(raw.valor_administracion) || 0;
    if (raw.renta_estimada !== undefined) doc.renta_estimada = Number(raw.renta_estimada) || 0;
    if (raw.descuento_porcentaje !== undefined) doc.descuento_porcentaje = Number(raw.descuento_porcentaje) || 0;
    if (raw.estrategiaComercial !== undefined) doc.estrategiaComercial = raw.estrategiaComercial;

    // 6. Comisiones (Overrides y General)
    if (raw.commissionRate !== undefined) doc.commissionRate = Number(raw.commissionRate) || 0;
    const commBroker = raw.comision_override_broker !== undefined ? raw.comision_override_broker : raw.brokerCommissionRate;
    if (commBroker !== undefined) {
        doc.comision_override_broker = commBroker !== null && commBroker !== '' ? Number(commBroker) : null;
    }
    const commPremium = raw.comision_override_premium !== undefined ? raw.comision_override_premium : raw.premiumCommissionRate;
    if (commPremium !== undefined) {
        doc.comision_override_premium = commPremium !== null && commPremium !== '' ? Number(commPremium) : null;
    }
    const commInterno = raw.comision_override_interno !== undefined ? raw.comision_override_interno : raw.commissionInterno;
    if (commInterno !== undefined) {
        doc.comision_override_interno = commInterno !== null && commInterno !== '' ? Number(commInterno) : null;
    }
    if (raw.tabla_comisiones !== undefined) {
        doc.tabla_comisiones = Array.isArray(raw.tabla_comisiones) ? raw.tabla_comisiones : null;
    }

    // 7. Especificaciones Físicas
    if (raw.num_habitaciones !== undefined) doc.num_habitaciones = Number(raw.num_habitaciones) || 0;
    if (raw.num_banos !== undefined) doc.num_banos = Number(raw.num_banos) || 0;
    if (raw.num_parqueaderos !== undefined) doc.num_parqueaderos = Number(raw.num_parqueaderos) || 0;
    if (raw.tipo_parqueadero !== undefined) doc.tipo_parqueadero = raw.tipo_parqueadero;
    if (raw.estrato !== undefined) doc.estrato = Number(raw.estrato) || 0;
    if (raw.piso !== undefined) doc.piso = raw.piso;
    if (raw.num_balcones !== undefined) doc.num_balcones = Number(raw.num_balcones) || 0;
    if (raw.ascensor !== undefined) {
        const ascVal = raw.ascensor === null || raw.ascensor === '' ? null : Number(raw.ascensor);
        doc.ascensor = ascVal;
        doc.num_ascensores = ascVal;
    } else if (raw.num_ascensores !== undefined) {
        const ascVal = raw.num_ascensores === null || raw.num_ascensores === '' ? null : Number(raw.num_ascensores);
        doc.ascensor = ascVal;
        doc.num_ascensores = ascVal;
    }

    // Área construida y privada
    let numAreaConstruida = raw.area_construida;
    if (numAreaConstruida === undefined && raw.area_construida_original !== undefined) {
        const parsed = parseFloat(String(raw.area_construida_original).replace(/m²|\s/g, ''));
        if (!isNaN(parsed)) numAreaConstruida = parsed;
    }
    if (numAreaConstruida !== undefined) {
        const val = Number(numAreaConstruida) || 0;
        doc.area_construida = val;
        doc.area = val;
        doc.area_construida_original = val > 0 ? `${val} m²` : '';
    } else if (raw.area_construida_original !== undefined) {
        doc.area_construida_original = String(raw.area_construida_original);
    }

    if (raw.area_privada !== undefined) {
        const rawPriv = String(raw.area_privada).replace(/m²|\s/g, '').trim();
        doc.area_privada = rawPriv ? `${rawPriv} m²` : null;
    }

    // Antigüedad y Año de Construcción
    const currentYear = new Date().getFullYear();
    if (raw.constructionYear !== undefined) {
        const year = raw.constructionYear ? Number(raw.constructionYear) : null;
        doc.anio_construccion = year;
        doc.antiguedad = year ? currentYear - year : null;
    } else if (raw.antiguedad !== undefined) {
        const ant = raw.antiguedad !== null && raw.antiguedad !== '' ? Number(raw.antiguedad) : null;
        doc.antiguedad = ant;
        if (ant !== null && ant >= 0) {
            doc.anio_construccion = currentYear - ant;
        }
    }

    // 8. Contacto y Vendedor
    const contactName = raw.nombre_cliente ?? raw.contacto_nombre ?? raw.nombre_contacto;
    if (contactName !== undefined) {
        const cVal = contactName ? String(contactName).trim() : null;
        doc.nombre_contacto = cVal;
        doc.contacto_nombre = cVal;
        doc.nombre_cliente = cVal;
    }
    const contactPhone = raw.telefono_cliente ?? raw.contacto_telefono ?? raw.celular_contacto ?? raw.telefono_contacto;
    if (contactPhone !== undefined) {
        const pVal = contactPhone ? String(contactPhone).trim() : null;
        doc.celular_contacto = pVal;
        doc.telefono_contacto = pVal;
        doc.contacto_telefono = pVal;
        doc.telefono_cliente = pVal;
    }
    const contactEmail = raw.correo_1 ?? raw.correo_contacto;
    if (contactEmail !== undefined) {
        const eVal = contactEmail ? String(contactEmail).trim() : null;
        doc.correo_contacto = eVal;
        doc.correo_1 = eVal;
    }

    // 9. Multimedia, Descripción & Features
    if (raw.descripcion !== undefined) doc.descripcion = raw.descripcion || '';
    const imgList = raw.images ?? raw.imagenes;
    if (imgList !== undefined) {
        const cleanImages = Array.isArray(imgList)
            ? imgList.map((img: any) => (typeof img === 'string' ? img : img?.url || '')).filter(Boolean)
            : [];
        doc.imagenes = cleanImages;
        doc.images = cleanImages;
    }
    const video = raw.video_youtube_url ?? raw.link_recorrido_virtual;
    if (video !== undefined) {
        doc.video_youtube_url = video || null;
        doc.link_recorrido_virtual = video || null;
    }
    if (raw.features !== undefined) {
        doc.features = Array.isArray(raw.features) ? raw.features : [];
    }
    if (raw.tag !== undefined || raw.tags !== undefined) {
        const cleanTags = normalizePropertyTags(raw.tag, raw.tags);
        doc.tag = cleanTags;
        doc.tags = cleanTags;
    }

    // 10. Operación, Visitas & Metadatos
    if (raw.es_visible_broker !== undefined) doc.es_visible_broker = Boolean(raw.es_visible_broker);
    if (raw.es_preventa !== undefined) doc.es_preventa = Boolean(raw.es_preventa);
    if (raw.destacado !== undefined) doc.destacado = Boolean(raw.destacado);
    if (raw.prioridad_global !== undefined) doc.prioridad_global = Number(raw.prioridad_global) || 10;
    if (raw.disponibilidad_visitas !== undefined) doc.disponibilidad_visitas = raw.disponibilidad_visitas || null;
    if (raw.observaciones_internas !== undefined) doc.observaciones_internas = raw.observaciones_internas || null;
    if (raw.numero_matricula !== undefined) doc.numero_matricula = raw.numero_matricula || null;
    if (raw.numero_predial !== undefined) doc.numero_predial = raw.numero_predial || null;
    if (raw.vigilanceType !== undefined || (raw as any).tipo_vigilancia !== undefined) {
        const vig = raw.vigilanceType ?? (raw as any).tipo_vigilancia;
        doc.tipo_vigilancia = vig || null;
        doc.vigilanceType = vig || null;
    }

    return doc;
};
